// src/components/pages/admin-dash/AdminSidebar.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Logo from "../../layout/Logo";
import Icon from "../../ui/Icon";
import axiosInstance from "../../../utils/AxiosInstance";
import { logoutUser } from "../../../redux/actions/userActions";

/**
 * Admin sidebar.
 *
 * Sits inside the AppShell, on a dark navy panel (using --panel token so
 * it looks the same in light + dark mode). Sections are collapsible
 * (state-driven, not the old `classList.toggle('hidden')` DOM hack).
 *
 * Props:
 *   activeView   string         current section key
 *   onSelect     fn(key)        called when a leaf item is clicked
 *   collapsed    bool           whether the sidebar is collapsed to icons
 *   onToggle     fn()           toggle collapsed state (mobile drawer close)
 *   userType     string         logged admin's type ("ADMIN"/"SUPPORT") — SUPPORT
 *                                only sees the "messages" group (backend-enforced,
 *                                this is just so the UI doesn't dangle on 403s)
 */
const NAV = [
  {
    id: "overview",
    label: "Overview",
    icon: "layout-dashboard",
    leaf: true,
  },
  {
    id: "users",
    label: "Users",
    icon: "users",
    children: [
      { id: "viewAllUsers",   label: "All users",       icon: "list" },
      { id: "adminAccess",    label: "Admins & Support", icon: "shield-lock" },
      { id: "bulkCredits",    label: "Bulk credits",    icon: "coin" },
      { id: "auditLog",       label: "Audit log",       icon: "shield-check" },
      { id: "auditActivitySummary", label: "Activity summary", icon: "chart-bar" },
    ],
  },
  {
    id: "payments",
    label: "Payments",
    icon: "credit-card",
    children: [
      { id: "listPaymentsByFilter", label: "By filter",      icon: "filter" },
      { id: "listPaymentsByUser",   label: "By user",        icon: "user-search" },
      { id: "updatePayment",        label: "Update payment", icon: "pencil" },
      { id: "webhookLog",           label: "Webhook log",    icon: "webhook" },
    ],
  },
  {
    id: "subscriptions",
    label: "Subscriptions",
    icon: "repeat",
    leaf: true,
  },
  {
    id: "reports",
    label: "Reports",
    icon: "chart-bar",
    children: [
      { id: "reportByParameter",     label: "By parameter",      icon: "sliders" },
      { id: "reportTopPrizes",       label: "Top prizes",        icon: "trophy" },
      { id: "reportHitDistribution", label: "Hit distribution",  icon: "chart-bar" },
      { id: "reportExtraBallImpact", label: "Extra ball impact", icon: "star" },
      { id: "reportFinancial",       label: "Financial",         icon: "trending-up" },
      { id: "reportRetention",       label: "Subscription retention", icon: "repeat" },
      { id: "reportScratchByState",  label: "Scratch-off by state",   icon: "ticket" },
    ],
  },
  {
    id: "draws",
    label: "Draws",
    icon: "ticket",
    children: [
      { id: "listAllDraws", label: "All draws",      icon: "list" },
      { id: "getDrawById",  label: "Find by ID",     icon: "search" },
      { id: "createDraw",   label: "Create draw",    icon: "plus" },
    ],
  },
  {
    id: "scratch",
    label: "Scratch-Offs",
    icon: "ticket",
    children: [
      { id: "scratchSources",  label: "Data sources",   icon: "database" },
      { id: "scratchIngest",   label: "Manual ingest",  icon: "refresh" },
      { id: "scratchSettings", label: "Settings",       icon: "settings" },
    ],
  },
  {
    id: "messages",
    label: "Contact inbox",
    icon: "mail",
    leaf: true,
  },
  {
    id: "mailingList",
    label: "Mailing list",
    icon: "mail",
    leaf: true,
  },
  {
    id: "system",
    label: "System",
    icon: "settings",
    children: [
      { id: "systemHealth", label: "System health", icon: "heartbeat" },
      { id: "cache",   label: "Cache",   icon: "database" },
      { id: "logging", label: "Logging", icon: "file-text" },
      { id: "globalConfiguration", label: "Global config", icon: "settings" },
      { id: "jobRuns", label: "Scheduled jobs", icon: "clock" },
      { id: "rateLimit", label: "Rate limit", icon: "gauge" },
    ],
  },
];

// Map each leaf view to its parent group so we know which group to auto-open
const PARENT_OF = (() => {
  const m = {};
  NAV.forEach((n) => {
    if (n.leaf) m[n.id] = n.id;
    n.children?.forEach((c) => (m[c.id] = n.id));
  });
  return m;
})();

export default function AdminSidebar({
  activeView,
  onSelect,
  collapsed = false,
  onToggle,
  userType,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const nav = userType === "SUPPORT"
    ? NAV.filter((item) => item.id === "messages")
    : NAV;

  // Track which sections are open. Auto-open the one containing activeView.
  const [openGroups, setOpenGroups] = useState(() => {
    const parent = PARENT_OF[activeView];
    return { [parent]: true };
  });

  useEffect(() => {
    const parent = PARENT_OF[activeView];
    if (parent) {
      setOpenGroups((g) => ({ ...g, [parent]: true }));
    }
  }, [activeView]);

  const toggleGroup = (id) =>
    setOpenGroups((g) => ({ ...g, [id]: !g[id] }));

  const handleLogout = async () => {
    try { await axiosInstance.post("/security/auth/logout"); } catch {}
    dispatch(logoutUser());
    window.dispatchEvent(new CustomEvent("auth:changed", {
      detail: { authenticated: false, source: "admin-logout" },
    }));
    navigate("/", { replace: true });
  };

  return (
    <aside
      className="flex flex-col shrink-0 transition-[width] duration-200"
      style={{
        width: collapsed ? 64 : 248,
        background: "var(--panel)",
        color: "var(--on-panel)",
        borderRight: "1px solid rgba(255,255,255,0.06)",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-4"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          minHeight: 56,
          color: "var(--on-panel)",
        }}
      >
        {collapsed ? (
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mx-auto p-1 rounded hover:opacity-80 transition-opacity"
            aria-label="LottoGPT — home"
            style={{ color: "var(--gold)" }}
          >
            <Icon name="lottogpt-mark" size={24} />
          </button>
        ) : (
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="inline-flex hover:opacity-80 transition-opacity"
              aria-label="LottoGPT — home"
              style={{ color: "var(--on-panel)" }}
            >
              <Logo size="sm" />
            </button>
            <span
              className="text-[10px] font-semibold tracking-[0.18em] uppercase"
              style={{ color: "var(--gold)" }}
            >
              Admin Console
            </span>
          </div>
        )}

        {/* Collapse / expand toggle */}
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            className="ml-auto p-1.5 rounded-md transition-colors"
            style={{
              color: "var(--on-panel-2)",
              background: "transparent",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <Icon name={collapsed ? "menu-2" : "x"} size={16} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
        {nav.map((item) => {
          if (item.leaf) {
            return (
              <NavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                active={activeView === item.id}
                collapsed={collapsed}
                onClick={() => onSelect(item.id)}
              />
            );
          }

          const open = !!openGroups[item.id];
          const childActive = item.children?.some((c) => c.id === activeView);

          return (
            <div key={item.id}>
              <button
                type="button"
                onClick={() => toggleGroup(item.id)}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg transition-colors"
                title={collapsed ? item.label : undefined}
                style={{
                  color: childActive ? "var(--on-panel)" : "var(--on-panel-2)",
                  background: childActive ? "rgba(255,255,255,0.06)" : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (!childActive)
                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                }}
                onMouseLeave={(e) => {
                  if (!childActive)
                    e.currentTarget.style.background = "transparent";
                }}
              >
                <Icon name={item.icon} size={16} />
                {!collapsed && (
                  <>
                    <span className="text-[13px] font-medium flex-1 text-left">
                      {item.label}
                    </span>
                    <span
                      className="text-[10px]"
                      style={{
                        color: "var(--on-panel-3)",
                        transform: open ? "rotate(180deg)" : "rotate(0)",
                        transition: "transform 0.15s",
                        display: "inline-block",
                      }}
                    >
                      ▾
                    </span>
                  </>
                )}
              </button>

              {!collapsed && open && (
                <div className="ml-2 mt-0.5 mb-1 space-y-0.5 pl-3"
                     style={{ borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
                  {item.children.map((c) => (
                    <NavItem
                      key={c.id}
                      icon={c.icon}
                      label={c.label}
                      active={activeView === c.id}
                      onClick={() => onSelect(c.id)}
                      small
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div
        className="px-2.5 py-3 space-y-0.5"
        style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
      >
        <NavItem
          icon="arrow-left"
          label="Back to site"
          collapsed={collapsed}
          onClick={() => navigate("/dashboard")}
        />
        <NavItem
          icon="logout"
          label="Log out"
          collapsed={collapsed}
          onClick={handleLogout}
          danger
        />
      </div>
    </aside>
  );
}

function NavItem({ icon, label, active, onClick, collapsed, small, danger }) {
  const baseColor = danger ? "var(--red)" : "var(--on-panel-2)";
  const activeColor = "#0E2A18";   // dark text on gold pill — same as Button gold
  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? label : undefined}
      className="w-full flex items-center gap-2 rounded-lg transition-colors"
      style={{
        padding: small ? "6px 10px" : "8px 10px",
        background: active ? "var(--gold)" : "transparent",
        color: active ? activeColor : baseColor,
        fontWeight: active ? 600 : 500,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.06)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      <Icon name={icon} size={small ? 13 : 16} />
      {!collapsed && (
        <span className={small ? "text-[12.5px]" : "text-[13px]"}>{label}</span>
      )}
    </button>
  );
}
